/* eslint-disable solid/no-innerhtml */
import {
  wpQuery,
  type companyTaxonomy,
  type companyType,
  type getTermQuery,
} from '@/lib/api/bapFormQuery'
import { CompanySchema, type getTerms } from '@/web-components/BapFormApp/BapFormSchema'
import { CompanyContact } from '@/web-components/BapFormApp/CompanyContact'
import { createEffect, createResource, createSignal, For, Match, Show, Switch } from 'solid-js'
import { z } from 'zod'
type TermTitleProps = {
  id: number
  companyTaxonomy: companyTaxonomy
}
function TermTitle(props: TermTitleProps) {
  const [term, setTerm] = createSignal<getTermQuery>()
  const [termData] = createResource(term, wpQuery.getTerm)
  createEffect(() => {
    setTerm({
      termId: props.id,
      path: props.companyTaxonomy,
      // query: { per_page: 100 },
    })
  })

  return (
    <>
      <Show when={termData()} keyed>
        {(t) => <span innerHTML={t.name} />}
      </Show>
    </>
  )
}

function validateIfArray(param: boolean | number[] | null | undefined | string) {
  if (!param) return false
  if (typeof param === 'boolean') return false
  if (typeof param === 'string') return false

  return param
}

type HtmlAttributes = {
  class?: string
  id?: string
}

async function fetchCompany(id: number) {
  try {
    if (!id) return
    const response = await wpQuery.getCompany({ id: id })
    const data = CompanySchema.parse(response)
    return data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

type CompanyProps = {
  companyId: number
  companyType: companyType
  categories: getTerms
} & HtmlAttributes
export function Company(props: CompanyProps) {
  const [companyId, setCompanyId] = createSignal<number>()
  const [company] = createResource(companyId, fetchCompany)
  createEffect(() => {
    setCompanyId(props.companyId)
  })

  return (
    <Show when={company()}>
      <div class={props.class}>
        <div class="space-y-16">
          <Switch>
            <Match when={props.companyType === 'bap_company' && company()} keyed>
              {(c) => <BapCompany categories={props.categories} company={c} />}
            </Match>
            <Match when={props.companyType === 'yp_company' && company()} keyed>
              {(c) => <YpCompany categories={props.categories} company={c} />}
            </Match>
          </Switch>
        </div>
      </div>
    </Show>
  )
}

type YpCompanyProps = {
  company: z.infer<typeof CompanySchema>
  categories: getTerms
}
function YpCompany(props: YpCompanyProps) {
  return (
    <>
      {/* title */}
      <h3 class="mt-0">
        <span class="sr-only">company</span>
        <div class="text-2xl font-bold">
          <span innerHTML={props.company?.title.rendered} />
        </div>
      </h3>
      {/* main category */}
      <Show when={validateIfArray(props.company?.acf?.yp_categories)} keyed>
        {(e) => (
          <>
            <div class="mt-2 font-bold text-yellow">
              <TermTitle companyTaxonomy="yp_category" id={e[0]} />
            </div>
          </>
        )}
      </Show>

      <div>
        {/* contact */}
        <Show when={props.company?.acf.yp_contact_info} keyed>
          {(contacts) => (
            <>
              <Show when={contacts[0]} keyed>
                {(mainContact) => <CompanyContact info={mainContact} />}
              </Show>
            </>
          )}
        </Show>

        {/* website */}
        <Show when={props.company?.acf?.website} keyed>
          {(e) => (
            <div>
              <span class="font-bold">Website: </span>
              {e}
            </div>
          )}
        </Show>
        <Show when={props.company.acf.yp_service_area}>
          <div>
            <span class="font-bold">Area: </span>
            {props.company.acf.yp_service_area}
          </div>
        </Show>
      </div>

      <Show when={props.company?.acf?.yp_description} keyed>
        {(e) => (
          <div>
            <span class="font-bold">Description: </span>
            <span innerHTML={e} />
          </div>
        )}
      </Show>

      <Show when={props.company.acf.yp_other_office_text}>
        <div class="font-bold">{props.company.acf.yp_other_office_text}</div>
      </Show>

      <For each={props.company.acf.yp_contact_info}>
        {(c, i) => (
          <>
            <Show when={i() !== 0}>
              <CompanyContact info={c} />
            </Show>
          </>
        )}
      </For>

      {/* other areas of services */}
      <Show
        when={
          validateIfArray(props.company?.acf?.yp_categories) &&
          validateIfArray(props.company?.acf?.yp_categories)
        }
        keyed
      >
        {(e) => (
          <Show when={e.length > 1 && e} keyed>
            {(z) => (
              <div>
                <span class="font-bold">Other areas of service:</span>
                <For each={z}>
                  {(y, i) => (
                    <>
                      <Show when={i() !== 0}>
                        <div>
                          <TermTitle companyTaxonomy={'yp_category'} id={y} />
                        </div>
                      </Show>
                    </>
                  )}
                </For>
              </div>
            )}
          </Show>
        )}
      </Show>
    </>
  )
}

type BapCompanyProps = {
  company: z.infer<typeof CompanySchema>
  categories: getTerms
}
function BapCompany(props: BapCompanyProps) {
  return (
    <>
      <div>
        {/* title */}

        <h3 class="mt-0">
          <div class="text-2xl font-bold">
            <span innerHTML={props.company?.title.rendered} />
            <span>{props.company?.acf?.bap_type && ` - ${props.company?.acf?.bap_type}`}</span>
          </div>
        </h3>

        {/* main category */}
        <Show when={validateIfArray(props.company?.acf?.bap_categories)} keyed>
          {(e) => {
            const termTitle = props.categories.find((cat) => cat.id === e[0])
            return (
              <>
                <div class="mt-2 font-bold text-yellow">
                  <span innerHTML={termTitle?.name} />
                </div>
              </>
            )
          }}
        </Show>
      </div>

      <div>
        {/* contact */}
        <Show when={props.company?.acf.bap_contact_info} keyed>
          {(contacts) => (
            <>
              <Show when={contacts[0]} keyed>
                {(mainContact) => <CompanyContact info={mainContact} />}
              </Show>
              <For each={contacts}>
                {(c, i) => (
                  <>
                    <Show when={i() !== 0}>
                      <CompanyContact info={c} />
                    </Show>
                  </>
                )}
              </For>
            </>
          )}
        </Show>

        {/* website */}
        <Show when={props.company?.acf?.website} keyed>
          {(e) => (
            <div>
              <span class="font-bold">Website: </span>
              {e}
            </div>
          )}
        </Show>
      </div>
      {/* bap description */}
      <Show when={props.company?.acf?.bap_description} keyed>
        {(e) => (
          <div>
            <span class="font-bold">Description: </span>
            <span innerHTML={e} />
          </div>
        )}
      </Show>

      {/* other areas of services */}
      <Show
        when={
          validateIfArray(props.company?.acf?.bap_categories) &&
          validateIfArray(props.company?.acf?.bap_categories)
        }
        keyed
      >
        {(e) => (
          <Show when={e.length > 1 && e} keyed>
            {(z) => (
              <div>
                <span class="font-bold">Other areas of service:</span>
                <For each={z}>
                  {(y, i) => {
                    const termTitle = props.categories.find((cat) => cat.id === y)
                    return (
                      <>
                        <Show when={i() !== 0}>
                          <div>
                            <span innerHTML={termTitle?.name} />
                          </div>
                        </Show>
                      </>
                    )
                  }}
                </For>
              </div>
            )}
          </Show>
        )}
      </Show>
    </>
  )
}
